/**
 * Taking a report off the screen and into a file.
 *
 * Two formats, and both are built here rather than fetched, because a till in Oyarifa with
 * the connection down still has to be able to hand the owner a figure.
 *
 *   PDF   is written straight to a file on a till, and goes through the browser's print to
 *         PDF everywhere else. No library and no server round trip either way, and both come
 *         out looking exactly like what is on the screen, which is the point: nobody has to
 *         trust that a second renderer agreed with the first.
 *
 *   Excel goes out as CSV with a UTF-8 byte order mark. Excel opens it by double click and
 *         keeps the accents in "Efua Mensimah"; without the BOM it mangles them. A real
 *         xlsx would need a zip writer and buys nothing here: every column is text or a
 *         number, there are no formulas and no formatting to preserve.
 */

import { printElement } from './print.js';
import { onTill, savePdf, reveal, can } from './till.js';

const A4 = 'size: A4 portrait; margin: 14mm;';

/**
 * The report as a PDF.
 *
 * On a till this writes a file, because that is what somebody asking for a PDF wants. The
 * print dialog asks for a printer first, and on a till the printer it offers is the receipt
 * printer, so a monthly report came out as four feet of 80mm paper.
 *
 * In a browser it stays the dialog, which is the only thing a page is allowed to do, and
 * "Save as PDF" is right there in it.
 *
 * Both paths go through the same print stylesheet, so the file and the dialog produce the
 * same document. Nobody has to trust that a second renderer agreed with the first.
 */
export async function exportPdf(element, title) {
  const dialog = () => {
    printElement(element, { title, page: A4 });
    return { saved: true, viaDialog: true };
  };

  // A browser has no file system, and a till whose shell predates the save can only do what
  // a browser can. Either way the dialog still works, and still has Save as PDF in it.
  if (!onTill || !can('savePdf')) return dialog();

  // The same state printElement puts the page into, held for the length of the render.
  const style = document.createElement('style');
  style.textContent = `@page { ${A4} }`;
  document.head.appendChild(style);
  element.classList.add('print-target');

  let result;
  try {
    result = await savePdf({ filename: title });
  } catch (e) {
    result = { saved: false, error: e.message };
  } finally {
    element.classList.remove('print-target');
    style.remove();
  }

  // Cancelling is a decision, so it stands. Anything else that failed falls back rather than
  // leaving somebody looking at a button that did nothing.
  if (!result.saved && !result.cancelled) return dialog();
  return result;
}

/**
 * Whether Download as PDF will write a file or fall back to the print dialog.
 *
 * The screens update themselves from the server and the shell around them does not, so a
 * till can be running a page that knows how to ask for a file inside an app that cannot
 * produce one. The button has to be able to say which it is about to do, because a dialog
 * asking for a printer is not what anybody pressing "download" wanted.
 */
export const pdfSavesAFile = () => onTill && can('savePdf');

/**
 * Save, and say where it went.
 *
 * A path in a sentence is not a file anybody found, so on success this opens the folder with
 * the file selected. The dialog path needs none of it: the browser already asked where.
 */
export async function exportPdfAndReveal(element, title) {
  const r = await exportPdf(element, title);
  if (r.saved && r.path) reveal(r.path);
  return r;
}

/**
 * A cell, escaped. Anything containing a comma, a quote or a newline is quoted, and inner
 * quotes are doubled, which is the whole of RFC 4180 that matters here.
 */
function cell(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const row = cells => cells.map(cell).join(',');

/** Money as a plain decimal, so Excel treats it as a number and can sum a column. */
const cedis = pesewas => (Number(pesewas || 0) / 100).toFixed(2);

/**
 * The report as a spreadsheet. One block per table, blank line between, which is how
 * somebody reads it and how Excel keeps the blocks apart.
 */
export function reportToCsv(r) {
  const lines = [];
  lines.push(row([r.title]));
  lines.push(row([`Period`, r.period.label]));
  lines.push(row([`Generated`, new Date().toLocaleString('en-GB')]));
  lines.push('');

  lines.push(row(['SUMMARY']));
  lines.push(row(['Takings (GHS)', cedis(r.totals.revenue)]));
  lines.push(row(['Sales', r.totals.sales]));
  lines.push(row(['Average sale (GHS)', cedis(r.totals.average)]));
  lines.push(row(['Commission (GHS)', cedis(r.totals.commission)]));
  lines.push(row(['Voided sales', r.totals.voided]));
  lines.push(row(['Voided amount (GHS)', cedis(r.totals.voidedAmount)]));
  lines.push('');

  // What each person delivered, one row per service, so a spreadsheet can pivot on either
  // the person or the service. This is the block somebody actually sorts and filters.
  if (r.byStaffService?.length) {
    lines.push(row(['WHAT EACH PERSON DID']));
    lines.push(row(['Staff', 'Service', 'Done', 'Takings (GHS)', 'Commission (GHS)']));
    for (const s of r.byStaffService) {
      lines.push(row([s.staff, s.service, s.qty ?? s.sold, cedis(s.revenue), cedis(s.commission)]));
    }
    lines.push('');
  }

  if (r.byStaff?.length) {
    lines.push(row(['TOTALS PER PERSON']));
    lines.push(row(['Staff', 'Done', 'Takings (GHS)', 'Commission (GHS)']));
    for (const s of r.byStaff) lines.push(row([s.staff, s.done ?? s.lines, cedis(s.revenue), cedis(s.commission)]));
    lines.push('');
  }

  if (r.byService?.length) {
    lines.push(row(['TOP SERVICES']));
    lines.push(row(['Service', 'Sold', 'Takings (GHS)']));
    for (const s of r.byService) lines.push(row([s.service, s.sold, cedis(s.revenue)]));
    lines.push('');
  }

  if (r.byPayment?.length) {
    lines.push(row(['HOW THEY PAID']));
    lines.push(row(['Method', 'Count', 'Amount (GHS)']));
    for (const p of r.byPayment) lines.push(row([p.method, p.count, cedis(p.amount)]));
    lines.push('');
  }

  if (r.byDay?.length) {
    lines.push(row(['BY DAY']));
    lines.push(row(['Day', 'Sales', 'Takings (GHS)']));
    for (const d of r.byDay) lines.push(row([d.day, d.sales, cedis(d.revenue)]));
  }

  return lines.join('\r\n');
}

/** Hand the browser a file. The BOM is what makes Excel read it as UTF-8. */
export function downloadCsv(filename, csv) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : filename + '.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download in some builds.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A filename somebody can find again: no spaces, no punctuation, the period in it. */
export function reportFilename(r) {
  return `${r.business.name} ${r.kind} ${r.period.label}`
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase();
}
